import { useState, useRef, useEffect, useMemo } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { CalendarDayNames, CalendarGrid, addMonths } from '@centraoneplus/ui';

// Parse/format YYYY-MM-DD <-> Date in LOCAL time (avoids the UTC day-shift that
// `new Date('YYYY-MM-DD')` introduces) so the calendar popup and the stored
// value string always agree. Kept local so this component has no dependency on
// any other date component.
function parseISODate(s: string): Date | null {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function formatISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export interface SingleDatePickerProps {
  /** Selected date, as YYYY-MM-DD. */
  value: string;
  /** Called when a date is chosen, with the new YYYY-MM-DD value. */
  onChange: (date: string) => void;
  /** Optional earliest selectable date, as YYYY-MM-DD. */
  minDate?: string;
  /** Optional latest selectable date, as YYYY-MM-DD. */
  maxDate?: string;
  /** Field label shown above the input (e.g. "Start Date"). */
  label?: string;
  className?: string;
  /**
   * Header-control look: a 34px, 10px-radius themed button with the label
   * inline as a mono prefix ("START  17/06/2026") instead of above it.
   */
  inline?: boolean;
  /** Which edge the calendar popover lines up with (default 'left'). */
  align?: 'left' | 'right';
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Field display, e.g. "17/06/2026" (DD/MM/YYYY).
function formatDisplayDate(s: string): string {
  const d = parseISODate(s);
  if (!d) return 'DD/MM/YYYY';
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

/** A drill-down calendar: day grid → click month for month list → click year for year list. */
function DrillDownCalendar({
  value,
  onSelect,
  minDate,
  maxDate,
}: {
  value?: Date;
  onSelect: (d: Date) => void;
  minDate?: Date;
  maxDate?: Date;
}) {
  const [view, setView] = useState<'days' | 'months' | 'years'>('days');
  const [viewDate, setViewDate] = useState<Date>(value ?? new Date());
  const [yearStart, setYearStart] = useState<number>(() => {
    const y = (value ?? new Date()).getFullYear();
    return Math.floor(y / 12) * 12;
  });

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const goPrev = () => {
    if (view === 'days') setViewDate(addMonths(viewDate, -1));
    else if (view === 'months') setViewDate(new Date(year - 1, month, 1));
    else setYearStart((s) => s - 12);
  };
  const goNext = () => {
    if (view === 'days') setViewDate(addMonths(viewDate, 1));
    else if (view === 'months') setViewDate(new Date(year + 1, month, 1));
    else setYearStart((s) => s + 12);
  };

  const openYears = () => {
    setYearStart(Math.floor(year / 12) * 12);
    setView('years');
  };

  const monthDisabled = (i: number) => {
    const first = new Date(year, i, 1);
    const last = new Date(year, i + 1, 0);
    if (minDate && last < minDate) return true;
    if (maxDate && first > maxDate) return true;
    return false;
  };
  const yearDisabled = (y: number) => {
    if (minDate && y < minDate.getFullYear()) return true;
    if (maxDate && y > maxDate.getFullYear()) return true;
    return false;
  };

  const navBtn = 'p-1.5 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors';
  const titleBtn = 'px-2 py-1 rounded-lg font-semibold text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors';
  const cellBtn = 'py-2 px-3 rounded-lg text-sm font-medium transition-colors';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 w-[300px]">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <button type="button" onClick={goPrev} className={navBtn} aria-label="Previous">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-1">
          {view === 'days' && (
            <>
              <button type="button" onClick={() => setView('months')} className={titleBtn}>
                {viewDate.toLocaleDateString(undefined, { month: 'long' })}
              </button>
              <button type="button" onClick={openYears} className={titleBtn}>
                {year}
              </button>
            </>
          )}
          {view === 'months' && (
            <button type="button" onClick={openYears} className={titleBtn}>
              {year}
            </button>
          )}
          {view === 'years' && (
            <span className="px-2 py-1 font-semibold text-gray-900 dark:text-gray-100">
              {yearStart} – {yearStart + 11}
            </span>
          )}
        </div>
        <button type="button" onClick={goNext} className={navBtn} aria-label="Next">
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Body */}
      {view === 'days' && (
        <>
          <CalendarDayNames />
          <CalendarGrid
            currentDate={viewDate}
            selectedDate={value}
            onDateSelect={onSelect}
            minDate={minDate}
            maxDate={maxDate}
          />
        </>
      )}

      {view === 'months' && (
        <div className="grid grid-cols-3 gap-2">
          {MONTHS_SHORT.map((m, i) => {
            const selected = value && value.getMonth() === i && value.getFullYear() === year;
            const disabled = monthDisabled(i);
            return (
              <button
                key={m}
                type="button"
                disabled={disabled}
                onClick={() => {
                  setViewDate(new Date(year, i, 1));
                  setView('days');
                }}
                className={`${cellBtn} ${
                  selected
                    ? 'bg-primary-600 text-white'
                    : disabled
                      ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                      : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {m}
              </button>
            );
          })}
        </div>
      )}

      {view === 'years' && (
        <div className="grid grid-cols-3 gap-2">
          {Array.from({ length: 12 }, (_, i) => yearStart + i).map((y) => {
            const selected = value && value.getFullYear() === y;
            const disabled = yearDisabled(y);
            return (
              <button
                key={y}
                type="button"
                disabled={disabled}
                onClick={() => {
                  setViewDate(new Date(y, month, 1));
                  setView('months');
                }}
                className={`${cellBtn} ${
                  selected
                    ? 'bg-primary-600 text-white'
                    : disabled
                      ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                      : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {y}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * A single-date field styled like a native date input (label above, boxed
 * MM/DD/YYYY value, calendar icon) that opens a drill-down calendar popup:
 * click the month to pick a month, click the year to pick a year.
 * Works in YYYY-MM-DD strings; pair two for an explicit start/end range.
 */
export function SingleDatePicker({
  value,
  onChange,
  minDate,
  maxDate,
  label,
  className = '',
  inline = false,
  align = 'left',
}: SingleDatePickerProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selected = useMemo(() => parseISODate(value) ?? undefined, [value]);
  const min = useMemo(() => (minDate ? parseISODate(minDate) ?? undefined : undefined), [minDate]);
  const max = useMemo(() => (maxDate ? parseISODate(maxDate) ?? undefined : undefined), [maxDate]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleSelect = (d: Date) => {
    onChange(formatISODate(d));
    setOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={ref}>
      {label && !inline && (
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
          {label}
        </label>
      )}
      {inline ? (
        <button
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={label ? `${label}: ${formatDisplayDate(value)}` : undefined}
          onClick={() => setOpen((v) => !v)}
          className="flex h-[34px] items-center gap-[8px] whitespace-nowrap rounded-[10px] border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[12.5px] text-[var(--co-ink)] transition-colors hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)] focus:border-[var(--co-brass)] focus:outline-none"
          style={open ? { borderColor: 'var(--co-brass)' } : undefined}
        >
          {label && (
            <span className="font-mono text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
              {label}
            </span>
          )}
          {formatDisplayDate(value)}
          <CalendarIcon className="h-[14px] w-[14px] shrink-0 text-[var(--co-ink-3)]" />
        </button>
      ) : (
        <button
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="flex items-center justify-between gap-3 w-44 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm shadow-sm transition-colors hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <span className="text-gray-900 dark:text-white whitespace-nowrap">{formatDisplayDate(value)}</span>
          <CalendarIcon className="w-4 h-4 text-gray-400 dark:text-gray-500 shrink-0" />
        </button>
      )}

      {open && (
        <div
          role="dialog"
          className={`absolute top-full ${align === 'right' ? 'right-0' : 'left-0'} mt-2 z-50 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800`}
        >
          <DrillDownCalendar value={selected} onSelect={handleSelect} minDate={min} maxDate={max} />
        </div>
      )}
    </div>
  );
}
